import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { type ReportingListSettlementReportsData, reportingListSettlementReportsOptions } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { ReportingTable } from '~/components/ReportingTable';
import { reportTableColumns } from '~/components/StatementTableColumns';
import { useNavigate } from '~/lib/navigation';
import { getReportTypeLabel } from '~/util/reporting';

type ReportType = NonNullable<NonNullable<ReportingListSettlementReportsData['query']>['type']>[number];

const BASE_REPORT_TYPES: ReportType[] = ['bank_account_summary', 'bank_account_transaction'];

const useHasReportType = (type: ReportType) => {
  const { data } = useQuery({
    ...reportingListSettlementReportsOptions({ query: { type: [type], limit: 1 } }),
  });
  return Boolean(data?.reports?.length);
};

export const PageReporting: React.FC = () => {
  const navigate = useNavigate();
  const hasLoanReports = useHasReportType('loan_daily_summary');
  const typeOptions = useMemo(
    () =>
      [...BASE_REPORT_TYPES, ...(hasLoanReports ? (['loan_daily_summary'] as const) : [])].map((value) => ({
        value,
        label: getReportTypeLabel(value),
      })),
    [hasLoanReports]
  );

  return (
    <>
      <PageActions>
        <Button type="button" onClick={() => navigate(`${ROUTE.REPORTING}/schedule`)} size="sm">
          <Icon.Plus />
          Schedule Report
        </Button>
      </PageActions>
      <ReportingTable
        tableId="reporting"
        filter={{ category: 'settlement' }}
        empty={{
          icon: <Icon.AnimationDocument />,
          title: 'No reports found',
          description: 'Schedule your first report to get started.',
          action: (
            <Button type="button" onClick={() => navigate(`${ROUTE.REPORTING}/schedule`)} size="sm">
              <Icon.Plus />
              Create Report
            </Button>
          ),
        }}
        className="pt-1"
        onRowClick={(entry) => navigate(`${ROUTE.REPORTING}/${entry.id}`)}
        columns={reportTableColumns}
        exactDateFilter
        typeOptions={typeOptions}
      />
    </>
  );
};
